/**
 * impostorWordHelpers.js
 *
 * Helper functions for ImpostorWord round
 */

import { doc, updateDoc, arrayUnion, onSnapshot, collection, setDoc, getDocs } from "firebase/firestore";
import { ImpostorGameTeams, TrustIssuesRoomCollection } from "../../../assets/DatabaseCollections";
import { db } from "../../../config/firebase";
import { movieTitlesForImpostorWordGame } from "../assets/Details";
import { fetchPlayersInTheLobby } from "../../playerHelpers";

/**
 * Listens to real-time updates for a given room.
 * @param {string} roomID
 * @param {Function} setRoomData
 * @returns {Function} unsubscribe listener
 */
export const listenToRoomData = (roomID, setRoomData) => {
    const roomRef = collection(db, TrustIssuesRoomCollection);
    const unsub = onSnapshot(roomRef, (snapshot) => {
        const roomDoc = snapshot.docs.find((doc) => doc.id === roomID);
        if (roomDoc) setRoomData({ id: roomDoc.id, ...roomDoc.data() });
    });
    return unsub;
};

/**
 * Fetches all team data for the Impostor Word game.
 * Firestore path: db / TrustIssuesRoomCollection / roomID / ImpostorGameTeams
 *
 * @param {string} roomID
 * @returns {Promise<Array>} array of teams [{ team, movie, impostor, members[] }]
 */
export const fetchImpostorGameTeams = async (roomID) => {
    try {
        const teamColRef = collection(
            db,
            TrustIssuesRoomCollection,
            roomID,
            "ImpostorGameTeams"
        );
        const snap = await getDocs(teamColRef);
        return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
        console.error("[ImpostorWord] Failed to fetch teams:", err);
        return [];
    }
};

/**
 * Checks if all impostors have submitted their guesses.
 * @param {Object} roomData
 * @returns {boolean} true if all impostors have submitted
 */
export const haveAllImpostorsSubmitted = (roomData) => {
    const impostors = roomData?.impostors || [];
    const guesses = roomData?.impostorGuesses || [];
    const submitted = guesses.map((g) => g.impostorID);
    return impostors.every((id) => submitted.includes(id));
};

/**
 * 📝 Submit impostor's guess.
 */
export const submitImpostorGuess = async (roomID, user, team, guess) => {
    if (!guess.trim()) return;
    const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
    await updateDoc(roomRef, {
        impostorGuesses: arrayUnion({
            impostorID: user.uid,
            team: team,
            guess: guess.trim(),
        }),
    });
};

/**
 * Assigns impostor game teams and writes them to Firestore.
 *
 * @param {string} roomID - The room ID.
 * @returns {Promise<Array>} - Returns an array of team structures.
 */
export const assignImpostorGameTeams = async (roomID) => {
    try {
        if (!roomID) throw new Error("Room ID is required.");

        // 1️⃣ Fetch all players in the lobby
        const players = await fetchPlayersInTheLobby(TrustIssuesRoomCollection, roomID);
        if (!players.length) throw new Error("No players found in this room.");

        // 2️⃣ Identify impostors and crewmates
        const impostorPlayers = players.filter((p) => p.impostor === true);
        const crewPlayers = players.filter((p) => !p.impostor);

        if (!impostorPlayers.length)
            throw new Error("No impostors found among players.");

        const numTeams = impostorPlayers.length;
        const teamNames = Array.from({ length: numTeams }, (_, i) =>
            String.fromCharCode(65 + i) // A, B, C...
        );

        // 3️⃣ Create team buckets (one impostor per team)
        const teams = teamNames.map((team, i) => ({
            team,
            impostor: impostorPlayers[i] || null,
            members: impostorPlayers[i] ? [impostorPlayers[i]] : [],
            movie: movieTitlesForImpostorWordGame[i]?.movie || "Unknown Movie",
        }));

        // 4️⃣ Evenly distribute crew across teams
        crewPlayers.forEach((crew, i) => {
            const teamIndex = i % numTeams;
            teams[teamIndex].members.push(crew);
        });

        // 5️⃣ Write to Firestore
        const teamCollectionRef = collection(
            db,
            TrustIssuesRoomCollection,
            roomID,
            ImpostorGameTeams
        );

        for (const team of teams) {
            const teamRef = doc(teamCollectionRef, team.team);
            await setDoc(teamRef, {
                team: team.team,
                movie: team.movie,
                impostor: team.impostor ? team.impostor.id || team.impostor.uid : null,
                members: team.members.map((m) => ({
                    id: m.id || m.uid,
                    name: m.name || m.displayName,
                    isImpostor: m.impostor || false,
                })),
            });
        }
        return teams;
    } catch (error) {
        console.error("[ImpostorGameTeams] Failed to assign teams:", error);
        throw error;
    }
};